import dayjs, { Dayjs } from "dayjs";

export interface FilmWindow {
  est_film_start?: string | null;
  est_film_end?: string | null;
  film_date?: string | null;
  film_days?: number | null;
  film_dates?: { film_date: string }[] | string[] | null;
  locked?: boolean | null;
}

// Mirrors FILM_WINDOW_EDITOR_USERTYPES on the API (admin / account manager
// admin / post production admin / pro team admin).
const FILM_WINDOW_EDITOR_ROLES = [
  "ADMIN",
  "ACCOUNT_MANAGER_ADMIN",
  "POST_PRODUCTION_ADMIN",
  "PRO_TEAM_ADMIN",
];

export const canEditFilmWindow = (role?: string | null) =>
  !!role && FILM_WINDOW_EDITOR_ROLES.includes(role);

export const hasFilmWindow = (window?: FilmWindow | null) =>
  !!window && !!window.est_film_start && !!window.est_film_end;

// An ISO timestamp (has a time part). Day-only strings ("YYYY-MM-DD") don't match.
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Calendar day for a film-schedule value.
 *
 * Day-only strings (est_film_start/end, film_dates rows, preferred_dates) parse
 * as a local day. Instants (brand_projects.film_date / film_date_end) are
 * stored by the API as UTC MIDNIGHT of the picked day, so they are read by
 * their UTC calendar day — parsing them as local time would show US viewers
 * the day BEFORE the shoot.
 */
export const toDay = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  if (typeof value === "string" && ISO_INSTANT.test(value)) {
    const instant = new Date(value);
    if (Number.isNaN(instant.getTime())) return null;
    return dayjs(
      new Date(
        instant.getUTCFullYear(),
        instant.getUTCMonth(),
        instant.getUTCDate()
      )
    );
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

/** Local-midnight Date for a film-schedule value (see toDay), or null. */
export const toDayDate = (value?: string | null): Date | null => {
  const day = toDay(value);
  return day ? day.toDate() : null;
};

export const isWithinFilmWindow = (
  value: Dayjs | string | null | undefined,
  window?: FilmWindow | null
) => {
  if (!hasFilmWindow(window)) return false;
  const day = typeof value === "string" ? toDay(value) : value?.startOf("day");
  const start = toDay(window!.est_film_start);
  const end = toDay(window!.est_film_end);
  if (!day || !start || !end) return false;
  return !day.isBefore(start) && !day.isAfter(end);
};

export const formatFilmWindow = (window?: FilmWindow | null) => {
  const start = toDay(window?.est_film_start);
  const end = toDay(window?.est_film_end);
  if (!start || !end) return "";
  // FRFD exact mode: a one-day window IS the hard filming date. The range
  // branch below renders it as "Mar 12 - 12, 2026", so short-circuit to the
  // single day.
  if (start.isSame(end, "day")) return start.format("MMM D, YYYY");
  const sameMonth = start.isSame(end, "month") && start.isSame(end, "year");
  return sameMonth
    ? `${start.format("MMM D")} - ${end.format("D, YYYY")}`
    : `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

export const formatFilmDay = (value?: string | null) => {
  const day = toDay(value);
  return day ? day.format("MMM D, YYYY") : "";
};

const toDayKey = (value?: Dayjs | string | null) => {
  if (!value) return "";
  const day = typeof value === "string" ? toDay(value) : value;
  return day ? day.format("YYYY-MM-DD") : "";
};

export const toDayList = (value?: unknown): string[] => {
  const rows = Array.isArray(value) ? value : [];
  const days = rows
    .map((row) => (typeof row === "string" ? row : (row as { film_date?: string })?.film_date))
    .map((day) => toDayKey(day))
    .filter((day): day is string => !!day);
  return [...new Set(days)].sort();
};

export const getFilmDays = (window?: FilmWindow | null): string[] =>
  toDayList(window?.film_dates);

export const getFilmDaysCount = (window?: FilmWindow | null) =>
  window?.film_days == null ? 1 : window.film_days;

export const FILM_DAYS_ERRORS: Record<string, string> = {
  FilmDaysTooManyDays: "You picked more days than the shoot runs.",
  FilmDateOutsideFilmWindow: "Pick days inside the filming range.",
  FilmDaysInvalidCount: "A shoot needs at least one filming day.",
  FilmDaysIncomplete: "Every filming day has to be set first.",
  FilmDateNotSet: "No filming date has been set yet.",
  ExpertsNotConfirmed:
    "Confirm at least one expert who has confirmed the filming date first.",
  SowsNotExecuted:
    "Every confirmed expert's SOW must be signed and countersigned before Pre-Production. The selection moves on its own once they are.",
  ConfirmedExpertRateRequired:
    "A confirmed expert needs a rate greater than zero (it is printed on their SOW).",
  ExpertHasNotConfirmed:
    "The expert has not confirmed the filming date yet.",
  SelectionLocked: "This selection is locked and cannot be rescheduled.",
  ShootLengthChanged:
    "The shoot length changed. Reload the page and try again.",
};

export const formatFilmDayList = (days: string[]) => {
  const parsed = days
    .map((day) => toDay(day))
    .filter((day): day is Dayjs => !!day)
    .sort((a, b) => a.valueOf() - b.valueOf());
  if (parsed.length === 0) return "";
  let lastMonth = "";
  const labels = parsed.map((day) => {
    const month = day.format("MMM YYYY");
    const label = month === lastMonth ? day.format("D") : day.format("MMM D");
    lastMonth = month;
    return label;
  });
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};
